import React, { FC } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@mui/material';
import { Box, DataTable, Typography, Widget } from '@scope/scope-one-ui/core';
import { COLORS, SIZES } from '@scope/scope-one-ui/theme';
import { compareTableCols, topCompaniesTableCols } from './columns';
import { AttentionBuzzChart } from './AttentionBuzzChart';
import { ChartBox } from './PeerGroupComparison.styled';
import {
  useGetCompareCompanyIndustry,
  useGetPeerGroupComparison,
  useGetTopCompanies,
} from '../../services/esgSentiment';
import { EsgSentimentParams } from '../../types/esgSentiment';
import { getCompareTableData, getTopCompaniesTableData } from './parsers';

export const PeerGroupComparison: FC = () => {
  const { entityId } = useParams<EsgSentimentParams>();
  const { t } = useTranslation();
  const { data: chartData, loading: chartLoading } = useGetPeerGroupComparison(entityId!);
  const { data: compareData, loading: compareLoading } = useGetCompareCompanyIndustry(entityId!);
  const { data: topData, loading: topLoading } = useGetTopCompanies(entityId!);

  const loading = compareLoading || topLoading || chartLoading;
  const company = compareData?.companyInfo.name;
  const sector = compareData?.sectorInfo.name;
  const industry = compareData?.industryInfo.name;
  const compareTableRows = getCompareTableData(compareData);
  const topCompaniesTableRows = getTopCompaniesTableData(topData);

  const companyScore = compareData?.companyInfo.todayScore.label.toLowerCase();
  const sectorScore = compareData?.sectorInfo.todayScore.label.toLowerCase();

  return (
    <Widget title={t('Peer Group Comparison')}>
      <Box display="flex" p={2}>
        <ChartBox
          flex={2}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          pr={2}
          gridGap={4}
        >
          <Typography variant="body1" color={COLORS.typography.main}>
            {loading ? (
              <Skeleton width={SIZES.spacing(25)} />
            ) : (
              t(
                `ESG sentiment around ${company} has been ${companyScore} in comparison to the ${sector} sector which has been ${sectorScore}.`,
              )
            )}
          </Typography>
          {loading ? (
            <Skeleton width="100%" height="100%" />
          ) : (
            <AttentionBuzzChart chartData={chartData!} />
          )}
        </ChartBox>
        <Box flex={3} display="flex" flexDirection="column" gridGap={4} pl={2}>
          <Box display="flex" flexDirection="column" gridGap={2}>
            <Typography variant="body1" color={COLORS.typography.label}>
              {loading ? (
                <Skeleton width={300} />
              ) : (
                t(`How does ${company} compare in its industry?`)
              )}
            </Typography>
            <Box>
              <DataTable
                cols={compareTableCols}
                rows={compareTableRows}
                loaderRowsCount={3}
                loading={loading}
              />
            </Box>
          </Box>
          <Box display="flex" flexDirection="column" gridGap={2}>
            <Typography variant="body1" color={COLORS.typography.label}>
              {loading ? (
                <Skeleton width={420} />
              ) : (
                t(`Top 3 companies within ${industry} industry (by ESG sentiment)`)
              )}
            </Typography>
            <Box>
              <DataTable
                cols={topCompaniesTableCols}
                rows={topCompaniesTableRows}
                loaderRowsCount={3}
                loading={loading}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Widget>
  );
};
